/**
 * Every event name the app sends, in one place. A call site imports `AnalyticsEvent.PickSubmitted`
 * rather than the string `"pick_submitted"` — the point isn't ceremony, it's that a typo in a
 * string literal fails silently (GA just shows an event nobody meant to create), while a typo in
 * a property name fails loudly at compile time.
 *
 * Adding a new event: add one line here, then `analytics.track(AnalyticsEvent.YourEvent, {...})`
 * at the call site. Nothing else needs to change — every registered provider receives it.
 */
export const AnalyticsEvent = {
  // Auth
  SignInAttempt: "sign_in_attempt",
  SignInSuccess: "sign_in_success",
  SignInFailure: "sign_in_failure",
  SignUpSuccess: "sign_up_success",
  PasswordResetRequested: "password_reset_requested",
  SignOut: "sign_out",
  ProfileCreated: "profile_created",

  // Competitions
  CompetitionCreateOpened: "competition_create_opened",
  CompetitionCreated: "competition_created",
  CompetitionJoinOpened: "competition_join_opened",
  CompetitionJoined: "competition_joined",
  CompetitionViewed: "competition_viewed",
  CompetitionTabChanged: "competition_tab_changed",
  CompetitionSettingsUpdated: "competition_settings_updated",

  // Picks
  PickSubmitted: "pick_submitted",

  // Admin
  AdminTabViewed: "admin_tab_viewed",
  AdminSyncTriggered: "admin_sync_triggered",
  AdminSuperAdminToggled: "admin_super_admin_toggled",

  // Platform / diagnostics — failures of every kind (a rejected mutation, an unhandled JS
  // error) funnel through trackApiError()/JsError with a `context` tag instead of one event
  // per action, so "everything that failed today" is a single filter rather than a dozen.
  WebVital: "web_vital",
  ApiError: "api_error",
  JsError: "javascript_error",
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
